import React, { useState } from 'react';
import { ArrowRight, CheckCircle, MapPin, Sparkles, Code, Cpu, Wand2, Send, Loader2, Shield } from 'lucide-react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

// API Key aus Umgebungsvariable
const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY || '';

const Hero: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -50]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedIdea, setGeneratedIdea] = useState('');
  const [showResult, setShowResult] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };
  
  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating) return;
    
    setIsGenerating(true);
    setShowResult(false);
    
    // Simuliere Ladezeit für bessere UX
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    try {
      if (!OPENROUTER_API_KEY) {
        // Intelligente Demo-Antworten basierend auf Keywords
        const demoIdeas = generateDemoIdea(prompt);
        setGeneratedIdea(demoIdeas);
        setShowResult(true);
        setIsGenerating(false);
        return;
      }
      
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': 'https://oetterliapps.ch',
          'X-Title': 'OetterliApps Business Generator'
        },
        body: JSON.stringify({
          model: 'anthropic/claude-3-haiku',
          messages: [{
            role: 'user',
            content: `Generiere eine innovative Geschäftsidee basierend auf: "${prompt}". 
Gib eine kurze, prägnante Antwort in 2-3 Sätzen. Fokussiere dich auf den Schweizer Markt und moderne Technologien. Sei kreativ und inspirierend!`
          }],
          temperature: 0.8,
          max_tokens: 200
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('OpenRouter Error:', errorData);
        throw new Error('API Error');
      }
      
      const data = await response.json();
      const idea = data.choices?.[0]?.message?.content || generateDemoIdea(prompt);
      
      setGeneratedIdea(idea);
      setShowResult(true);
    } catch (error) {
      console.error('Generation error:', error);
      setGeneratedIdea(generateDemoIdea(prompt));
      setShowResult(true);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const generateDemoIdea = (input: string) => {
    const lowercaseInput = input.toLowerCase();
    
    // Keyword-basierte intelligente Antworten
    if (lowercaseInput.includes('handwerk')) {
      return `🚀 **HANDWERKER-PLATTFORM MIT KI**

🎟 **Core Features:**
• KI-Terminplanung & Routenoptimierung
• AR-Kostenvoranschläge per Kamera
• Automatische Kundenmatching
• Digitale Auftragsabwicklung
• Echtzeit-Materialverwaltung

💰 **Entwicklungskosten:** CHF 45'000 - 75'000
📅 **Zeitrahmen:** 3-4 Monate
🔄 **Wartung:** CHF 800/Monat`;
    }
    
    if (lowercaseInput.includes('nachhaltig') || lowercaseInput.includes('umwelt')) {
      return `🌱 **NACHHALTIGKEITS-APP BASEL**

🎟 **Core Features:**
• KI-basiertes CO2-Tracking
• Lokale Öko-Marktplatz Integration
• Gamification & Belohnungssystem
• Community Challenges
• Nachhaltigkeits-Score & Badges

💰 **Entwicklungskosten:** CHF 35'000 - 55'000
📅 **Zeitrahmen:** 2-3 Monate
🔄 **Wartung:** CHF 600/Monat`;
    }
    
    if (lowercaseInput.includes('gastro') || lowercaseInput.includes('restaurant')) {
      return `🍴 **GASTRO-KI FÜR RESTAURANTS**

🎟 **Core Features:**
• KI-Bestellvorhersage & Inventar
• Automatische Menüoptimierung
• Gästezahl-Prognose
• Zero-Waste Management
• QR-Menü & Tischreservierung

💰 **Entwicklungskosten:** CHF 55'000 - 85'000
📅 **Zeitrahmen:** 3-5 Monate
🔄 **Wartung:** CHF 1'200/Monat`;
    }
    
    if (lowercaseInput.includes('gesundheit') || lowercaseInput.includes('fitness')) {
      return `💪 **GESUNDHEITS-KI MIT KK-INTEGRATION**

🎟 **Core Features:**
• Personalisierter KI-Fitness-Coach
• Krankenversicherungs-API
• Prämienrabatt-System
• Gesundheitsdaten-Tracking
• Telemedizin-Integration

💰 **Entwicklungskosten:** CHF 65'000 - 95'000
📅 **Zeitrahmen:** 4-6 Monate
🔄 **Wartung:** CHF 1'500/Monat`;
    }
    
    if (lowercaseInput.includes('bildung') || lowercaseInput.includes('lernen')) {
      return `🎓 **ADAPTIVE LERNPLATTFORM SCHWEIZ**

🎟 **Core Features:**
• KI-Tutor (DE/FR/IT)
• Personalisierte Lernpfade
• Fortschritts-Analytics
• Eltern-Dashboard
• Gamifiziertes Lernen

💰 **Entwicklungskosten:** CHF 50'000 - 80'000
📅 **Zeitrahmen:** 3-5 Monate
🔄 **Wartung:** CHF 1'000/Monat`;
    }
    
    if (lowercaseInput.includes('immobilien') || lowercaseInput.includes('wohnung')) {
      return `🏠 **IMMOBILIEN-KI PLATTFORM**

🎟 **Core Features:**
• 360° Virtuelle Besichtigungen
• KI-Preisbewertung
• Smart Contract Mietverträge
• Automatisches Matching
• Blockchain-Dokumentation

💰 **Entwicklungskosten:** CHF 70'000 - 120'000
📅 **Zeitrahmen:** 4-6 Monate
🔄 **Wartung:** CHF 1'800/Monat`;
    }
    
    if (lowercaseInput.includes('basel')) {
      return `🇫🇦 **BASEL CITY APP MIT KI**

🎟 **Core Features:**
• KI-Event-Empfehlungen
• AR-Stadtführungen
• Lokale Business Integration
• FCB Digital Fan-Features
• Community Marketplace

💰 **Entwicklungskosten:** CHF 40'000 - 65'000
📅 **Zeitrahmen:** 3-4 Monate
🔄 **Wartung:** CHF 900/Monat`;
    }
    
    // Fallback für allgemeine Anfragen
    return `🚀 **${input.toUpperCase()}-APP MIT KI**

🎟 **Core Features:**
• KI-gestützte Automatisierung
• Benutzerfreundliches Dashboard
• Cloud-Synchronisation
• API-Integrationen
• Analytics & Reporting

💰 **Entwicklungskosten:** CHF 30'000 - 60'000
📅 **Zeitrahmen:** 2-4 Monate
🔄 **Wartung:** CHF 500-1'000/Monat`;
  };

  return (
    <section className="pt-32 pb-20 md:pt-40 md:pb-32 relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <motion.div className="absolute inset-0 z-0" style={{ opacity }}>
        <motion.div 
          className="absolute top-20 right-20 w-96 h-96 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 blur-3xl opacity-20"
          style={{ y: y1 }}
        />
        <motion.div 
          className="absolute bottom-10 left-10 w-96 h-96 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 blur-3xl opacity-20"
          style={{ y: y2 }}
        />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100/30 to-blue-100/30"></div>
        </div>
      </motion.div>
      
      <motion.div 
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="container mx-auto px-4 relative z-10"
      >
        <div className="flex flex-col lg:flex-row items-center">
          <div className="lg:w-1/2 lg:pr-12 mb-12 lg:mb-0">
            <motion.div 
              variants={itemVariants}
              className="flex flex-wrap items-center gap-4 mb-8"
            >
              <motion.div 
                className="flex items-center bg-gray-100 text-gray-800 rounded-full px-5 py-2.5"
              >
                <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                <span className="text-sm font-semibold">Zertifizierter Schweizer Entwickler</span>
              </motion.div>
              <motion.div 
                className="flex items-center bg-white rounded-full px-5 py-2.5 shadow-sm border border-gray-200"
              >
                <MapPin className="w-4 h-4 mr-2 text-red-500" />
                <span className="text-sm font-semibold text-gray-800">Standort Basel</span>
              </motion.div>
              <motion.div 
                className="flex items-center bg-gray-100 text-gray-800 rounded-full px-5 py-2.5"
              >
                <Shield className="w-4 h-4 mr-2 text-blue-600" />
                <span className="text-sm font-semibold">DSGVO-konform</span>
              </motion.div>
            </motion.div>

            <motion.div 
              variants={itemVariants}
              className="mb-6"
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="block text-gray-900">Software Entwicklung</span>
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">Basel & Schweiz</span>
                <span className="block text-gray-900">mit KI-Power.</span>
              </h1>
            </motion.div>
            
            <motion.p 
              variants={itemVariants}
              className="text-xl md:text-2xl text-gray-600 mb-8 max-w-xl"
            >
              Dein <span className="font-semibold text-gray-900">App Entwickler in Basel</span> für massgeschneiderte
              <span className="font-semibold text-gray-900"> Webseiten</span>,
              <span className="font-semibold text-gray-900"> Software-Lösungen</span> und
              <span className="font-semibold text-gray-900"> KI-Automatisierung</span> für Schweizer KMU.
              <span className="block mt-4 text-lg text-gray-500">
                Digitalisierung Basel: Spare täglich 2-3 Stunden mit intelligenter 
                <span className="font-semibold text-gray-700"> Prozessautomatisierung.</span>
              </span>
            </motion.p>

            <motion.div 
              variants={itemVariants}
              className="grid grid-cols-3 gap-6 mb-12 max-w-lg"
            >
              <motion.div 
                whileHover={{ y: -5 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-gray-900">10+</div>
                <div className="text-sm text-gray-600">Projekte in Basel</div>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="text-center border-x border-gray-200"
              >
                <div className="text-3xl font-bold text-gray-900">100%</div>
                <div className="text-sm text-gray-600">Schweizer Qualität</div>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-gray-900">24h</div>
                <div className="text-sm text-gray-600">Response Time</div>
              </motion.div>
            </motion.div>

            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/termin-buchen">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative px-8 py-4 bg-black text-white font-semibold rounded-full overflow-hidden transition-all duration-300 hover:bg-gray-900"
                >
                <span className="relative z-10 flex items-center">
                  Jetzt Beratungsgespräch sichern
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
                </motion.button>
              </Link>
              <motion.a
                href="#projects"
                whileHover={{ scale: 1.05 }}
                className="px-8 py-4 font-semibold text-gray-700 rounded-full border-2 border-gray-300 hover:border-gray-400 transition-colors duration-300 flex items-center"
              >
                Projekte ansehen
                <Code className="ml-2 h-5 w-5" />
              </motion.a>
            </motion.div>

          </div>
          
          <motion.div 
            variants={itemVariants}
            className="lg:w-1/2 relative"
          >
            <motion.div 
              animate={floatingAnimation}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 p-1 shadow-2xl">
                <div className="rounded-3xl bg-gray-900 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <motion.div 
                      className="text-xs text-gray-400 font-mono"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      KI-APP
                    </motion.div>
                  </div>
                  
                  <div className="space-y-4">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-center mb-6"
                    >
                      <h3 className="text-xl font-bold text-white mb-2 flex items-center justify-center gap-2">
                        <Wand2 className="w-5 h-5 text-purple-400" />
                        Schreibe deine Idee in die KI
                        <Sparkles className="w-5 h-5 text-yellow-400" />
                      </h3>
                      <p className="text-sm text-gray-400">Erhalte Features, Funktionen und Preisschätzung</p>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                      className="space-y-3"
                    >
                      <div className="relative">
                        <input
                          type="text"
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleGenerate()}
                          placeholder="z.B. 'Nachhaltiges Business in Basel' oder 'App für Handwerker'"
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-12"
                          disabled={isGenerating}
                        />
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={handleGenerate}
                          disabled={!prompt.trim() || isGenerating}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-purple-400 hover:text-purple-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isGenerating ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Send className="w-5 h-5" />
                          )}
                        </motion.button>
                      </div>
                      
                      {!showResult && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.9 }}
                          className="flex flex-wrap gap-2"
                        >
                          <span className="text-xs text-gray-500">Beispiele:</span>
                          {['Handwerker App', 'Nachhaltiges Basel', 'Gastro Innovation'].map((example) => (
                            <motion.button
                              key={example}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setPrompt(example)}
                              className="text-xs px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-md border border-gray-700 hover:border-gray-600 transition-all"
                            >
                              {example}
                            </motion.button>
                          ))}
                        </motion.div>
                      )}
                      
                      <AnimatePresence>
                        {showResult && generatedIdea && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <motion.div
                              initial={{ y: 20 }}
                              animate={{ y: 0 }}
                              className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 backdrop-blur-sm rounded-xl p-4 border border-purple-700/50"
                            >
                              <div className="flex items-start gap-3">
                                <motion.div
                                  animate={{ rotate: [0, 360] }}
                                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                >
                                  <Sparkles className="w-5 h-5 text-yellow-400 mt-1" />
                                </motion.div>
                                <div className="flex-1">
                                  <h4 className="text-sm font-semibold text-purple-300 mb-2">Deine App-Entwicklung im Detail:</h4>
                                  <div className="text-white text-sm leading-relaxed whitespace-pre-line">{generatedIdea}</div>
                                </div>
                              </div>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      
                      <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mt-4">
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          Live KI
                        </span>
                        <span>•</span>
                        <span>Powered by Gemini</span>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              <motion.div 
                initial={{ opacity: 0, scale: 0 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 2, type: "spring" }}
                className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-2xl p-6 border border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Kundenurteil</div>
                    <div className="text-xl font-bold text-gray-900">"Beste Investition!"</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;